import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useActions, useMe, type Action } from "../api/hooks";
import { Chip, Empty, ErrorBox, Loading, Page, Table, Tabs, type Tone } from "../components/ui";
import { fmtDate, fmtNumber, relative, title } from "../lib/format";

export const Route = createFileRoute("/impact")({ component: ImpactHistory });

type Measured = { signal_key?: string; before?: number; after?: number; delta?: number; improved?: boolean; measured_period_start?: string };
type Baseline = { signal_key?: string; value?: number; period_start?: string };

type Row = {
  action: Action;
  measured: Measured | null;
  baseline: Baseline | null;
  status: "improved" | "no_change" | "waiting" | "unmeasured";
  daysToOutcome: number | null;
};

function classify(a: Action): Row {
  const measured = (a.measured_outcome ?? null) as Measured | null;
  const baseline = (a.baseline ?? null) as Baseline | null;
  const has = !!measured && measured.before != null && measured.after != null;
  const status: Row["status"] = has ? (measured!.improved ? "improved" : "no_change") : baseline ? "waiting" : "unmeasured";
  const daysToOutcome = a.completed_at && a.created_at ? Math.max(0, Math.round((Date.parse(a.completed_at) - Date.parse(a.created_at)) / 86_400_000)) : null;
  return { action: a, measured, baseline, status, daysToOutcome };
}

const tone: Record<Row["status"], Tone> = { improved: "good", no_change: "high", waiting: "elevated", unmeasured: "neutral" };
const label: Record<Row["status"], string> = { improved: "Improved", no_change: "No improvement", waiting: "Measuring", unmeasured: "No baseline" };

function ImpactHistory() {
  const me = useMe();
  const isAdmin = me.data?.roles.some((r) => r === "hr_admin" || r === "platform_admin") ?? false;
  const closed = useActions({ state: "completed" });
  const [tab, setTab] = useState<"all" | "improved" | "no_change" | "waiting">("all");
  const rows = useMemo(() => (closed.data ?? []).map(classify).sort((a, b) => ((a.action.completed_at ?? "") < (b.action.completed_at ?? "") ? 1 : -1)), [closed.data]);
  const counts = {
    improved: rows.filter((r) => r.status === "improved").length,
    no_change: rows.filter((r) => r.status === "no_change").length,
    waiting: rows.filter((r) => r.status === "waiting").length,
  };
  const measured = counts.improved + counts.no_change;
  const shown = tab === "all" ? rows : rows.filter((r) => r.status === tab);
  const days = rows.map((r) => r.daysToOutcome).filter((d): d is number => d != null);
  const medianDays = days.length ? [...days].sort((a, b) => a - b)[Math.floor(days.length / 2)] : null;
  // The interventions worth repeating: improved, grouped by what they were about.
  const repeatable = useMemo(() => {
    const m = new Map<string, { n: number; example: Row }>();
    for (const r of rows.filter((r) => r.status === "improved")) {
      const k = r.measured?.signal_key ?? r.action.source_signal_key ?? "other";
      const cur = m.get(k);
      m.set(k, { n: (cur?.n ?? 0) + 1, example: cur?.example ?? r });
    }
    return [...m.entries()].sort((a, b) => b[1].n - a[1].n).slice(0, 4);
  }, [rows]);

  if (!isAdmin) return <Page title="Impact history"><Empty title="Admins only" body="Managers see the outcomes of their own actions on Actions → Closed." /></Page>;
  return (
    <Page title="Impact history" sub="Every completed intervention: the issue, what was done, and whether the signal behind it moved. Measured 14 days after completion.">
      {closed.isError && <div className="mb-3"><ErrorBox error={closed.error} /></div>}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="card kpi"><h4>Completed interventions</h4><div className="v">{rows.length}</div><div className="delta">{counts.waiting} still inside the measurement window</div></div>
        <div className="card kpi">
          <h4>Moved the signal</h4>
          <div className="v"><span>{measured ? `${Math.round((100 * counts.improved) / measured)}%` : "—"}</span>{measured > 0 && <Chip tone={counts.improved / measured >= 0.6 ? "good" : counts.improved / measured >= 0.4 ? "elevated" : "high"}>{counts.improved} of {measured}</Chip>}</div>
          <div className="delta">measured actions whose signal improved</div>
        </div>
        <div className="card kpi"><h4>Time to outcome</h4><div className="v">{medianDays != null ? `${medianDays} d` : "—"}</div><div className="delta">median, from action created to completed</div></div>
        <div className="card kpi"><h4>No improvement</h4><div className="v">{counts.no_change}</div><div className="delta">worth a different approach next time</div></div>
      </div>

      {repeatable.length > 0 && (
        <div className="card mb-4">
          <h4 className="font-bold text-[13.5px]">Worth repeating</h4>
          <div className="grid grid-cols-4 gap-3 mt-3">
            {repeatable.map(([k, v]) => (
              <div key={k} className="text-[12.5px]">
                <div className="font-semibold">{v.example.action.title}</div>
                <div className="sub">{title(k).replace(" Pct", " %")} · improved {v.n} time{v.n === 1 ? "" : "s"}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Tabs
        tabs={[
          { key: "all", label: "All", count: rows.length },
          { key: "improved", label: "Improved", count: counts.improved },
          { key: "no_change", label: "No improvement", count: counts.no_change },
          { key: "waiting", label: "Measuring", count: counts.waiting },
        ]}
        active={tab}
        onChange={setTab}
      />
      {closed.isPending ? <Loading /> : shown.length === 0 ? (
        <Empty title={rows.length === 0 ? "Nothing completed yet" : "Nothing in this view"} body={rows.length === 0 ? "Impact history fills in as actions are completed and their signals re-measured." : undefined} action={<Link to="/actions" className="link">Go to actions →</Link>} />
      ) : (
        <div className="mt-3">
          <Table
            rows={shown}
            keyOf={(r) => r.action.id}
            cols={[
              { h: "Original issue", cell: (r) => <><span className="font-semibold">{r.action.source_signal_key ? title(r.action.source_signal_key).replace(" Pct", " %") : title(r.action.source)}</span><span className="sub">{title(r.action.audience_type)} · {r.action.audience_key}</span></> , w: "200px" },
              { h: "Action taken", cell: (r) => <><span className="font-semibold">{r.action.title}</span>{r.action.rationale && <span className="sub">{r.action.rationale}</span>}</> },
              { h: "Expected", cell: (r) => <span className="text-muted">{r.action.expected_outcome ?? "—"}</span>, w: "160px" },
              {
                h: "Actual",
                w: "220px",
                cell: (r) => {
                  if (r.status === "improved" || r.status === "no_change") {
                    const unit = r.measured?.signal_key?.endsWith("_pct") ? "%" : "";
                    return <div><div className="font-semibold">{fmtNumber(r.measured!.before!)}{unit} → {fmtNumber(r.measured!.after!)}{unit}</div><span className="sub">{title(r.measured?.signal_key ?? "").replace(" Pct", " %")}{r.measured?.measured_period_start ? ` · week of ${fmtDate(r.measured.measured_period_start)}` : ""}</span></div>;
                  }
                  if (r.status === "waiting") return <span className="text-muted">Baseline {r.baseline?.value != null ? fmtNumber(r.baseline.value) : "set"}; measured 14 days after completion</span>;
                  return <span className="text-muted">No baseline was recorded, so this one cannot be measured</span>;
                },
              },
              { h: "Outcome", cell: (r) => <Chip tone={tone[r.status]}>{label[r.status]}</Chip>, w: "140px" },
              { h: "Time to outcome", cell: (r) => <span className="text-muted">{r.daysToOutcome != null ? `${r.daysToOutcome} d` : "—"}</span>, w: "120px" },
              { h: "Completed", cell: (r) => <span className="text-muted" title={r.action.completed_at ?? ""}>{relative(r.action.completed_at)}</span>, w: "110px" },
            ]}
          />
        </div>
      )}
    </Page>
  );
}
